var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animated, useSpring } from '@react-spring/web';
import styled from 'styled-components';
import cx from 'classnames';
import useOnClickOutside from '../hooks/use-click-outside';
import { CloseIcon } from '../close-icon/close-icon';
import { borderRadius } from '../theme/border-radius';
import { shadow } from '../theme/shadow';
const StyledModalWrapper = styled.div `
  width: 600px;
  height: 300px;
  position: fixed;
  left: 0px;
  right: 0px;
  top: 0px;
  bottom: 0px;
  margin: auto auto;
  pointer-events: none;
`;
const StyledModal = styled(animated.div) `
  width: 100%;
  height: 100%;
  box-shadow: ${shadow.lg};
  border-radius: ${borderRadius.lg};
  z-index: -1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${() => 'var(--bg-color)'};
  color: ${() => 'var(--text-color-0)'};
  pointer-events: all;

  div[data-role='modal-body'] {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
  }
`;
const StyledMask = styled(animated.div) `
  position: fixed;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  background-color: rgba(0, 0, 0, 0.65);
`;
const StyledModalBar = styled.div `
  height: 52px;
  padding: 8px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: ${() => 'var(--bg-color-1)'};
  flex: 0;
  box-shadow: ${shadow.sm};
`;
export const Modal = React.forwardRef((_a, ref) => {
    var { closeable = true, maskCloseable = true, maskable = true } = _a, props = __rest(_a, ["closeable", "maskCloseable", "maskable"]);
    const modalRef = useRef();
    const maskRef = useRef();
    const [visible, setVisible] = useState(true);
    const controlled = typeof props.visible === 'boolean';
    const finalVisible = useMemo(() => {
        return controlled ? props.visible : visible;
    }, [visible, controlled, props.visible]);
    const handleToggleModal = useCallback(() => {
        var _a, _b;
        setVisible((prev) => !prev);
        (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props, !!finalVisible);
        (_b = props.onMask) === null || _b === void 0 ? void 0 : _b.call(props, !!finalVisible);
    }, [props.onClose, props.onMask, controlled]);
    const includes = useMemo(() => {
        if (maskCloseable) {
            return [];
        }
        return [maskRef];
    }, [maskRef, maskCloseable]);
    useOnClickOutside(modalRef, handleToggleModal, { disable: !finalVisible, includes });
    const modal = useSpring({
        scale: finalVisible ? 1 : 0,
        opacity: finalVisible ? 1 : 0,
    });
    const hasTitle = closeable || props.title !== undefined;
    return createPortal(React.createElement(StyledModalWrapper, { ref: ref, className: cx('modal-container', props.className) },
        React.createElement(StyledModal, { "data-role": "modal", className: "modal", ref: (dom) => (modalRef.current = dom), style: Object.assign(Object.assign({}, modal), { zIndex: modal.opacity.to((x) => (x === 0 ? -1 : 1000)) }) },
            hasTitle ? (React.createElement(StyledModalBar, { className: "modal-title", "data-role": "modal-title", style: props.style }, closeable ? React.createElement(CloseIcon, { position: "relative", onClick: handleToggleModal }) : null)) : null,
            React.createElement("div", { className: "modal-body", "data-role": "modal-body", style: props.bodyStyle }, props.children)),
        maskable && (React.createElement(StyledMask, { ref: (dom) => (maskRef.current = dom), "data-role": "modal-mask", className: "modal-mask", onClick: handleToggleModal, style: {
                pointerEvents: finalVisible ? undefined : 'none',
                opacity: modal.opacity,
                zIndex: modal.opacity.to((x) => (x === 0 ? -1 : 999)),
            } }))), document.body);
});
